import React, { useEffect, useMemo, useState } from "react";
import { Crown } from "lucide-react";
import { formatMoney, CODE } from "../mock";
import { useLeaderboard } from "../hooks/useLeaderboard";
import { CutEdge } from "./Hero";

/* Empty slots (fewer than 10 players) display an em dash */
const show = (v) => (v == null ? "—" : formatMoney(v));
const showUser = (u) => (u == null ? "—" : u);
const initial = (u) => (u == null ? "?" : u[0].toUpperCase());

const useCountdown = (endIso) => {
  const target = useMemo(() => {
    if (endIso) return new Date(endIso);
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth() + 1, 1, 0, 0, 0);
  }, [endIso]);
  const [left, setLeft] = useState(target - new Date());
  useEffect(() => {
    const t = setInterval(() => setLeft(Math.max(0, target - new Date())), 1000);
    return () => clearInterval(t);
  }, [target]);
  const d = Math.floor(left / 86400000);
  const h = Math.floor((left % 86400000) / 3600000);
  const m = Math.floor((left % 3600000) / 60000);
  const s = Math.floor((left % 60000) / 1000);
  return { d, h, m, s };
};

const pad = (n) => String(n).padStart(2, "0");

const avatarBg = (name) => {
  if (name == null) return "linear-gradient(135deg, #39445e, #232a3d)";
  const palette = [
    ["#8b5cf6", "#6d28d9"],
    ["#38bdf8", "#0284c7"],
    ["#f472b6", "#be185d"],
    ["#fbbf24", "#d97706"],
    ["#34d399", "#059669"],
    ["#f87171", "#b91c1c"],
  ];
  const idx = name.split("").reduce((a, c) => a + c.charCodeAt(0), 0) % palette.length;
  return `linear-gradient(135deg, ${palette[idx][0]}, ${palette[idx][1]})`;
};

/* ---------- Podium (top 3) ---------- */
const PODIUM = {
  1: {
    height: "h-28 sm:h-36",
    ring: "ring-[3px] ring-[#ffd76b]",
    plate: "from-[#ffd76b] to-[#e3a93c]",
    text: "text-[#ffd76b]",
    glow: "shadow-[0_0_50px_rgba(255,215,107,0.25)]",
    avatar: "w-24 h-24 text-4xl",
  },
  2: {
    height: "h-20 sm:h-24",
    ring: "ring-2 ring-[#c3cde2]",
    plate: "from-[#d7deec] to-[#9aa7c0]",
    text: "text-[#c3cde2]",
    glow: "shadow-[0_0_34px_rgba(195,205,226,0.16)]",
    avatar: "w-[74px] h-[74px] text-3xl",
  },
  3: {
    height: "h-14 sm:h-16",
    ring: "ring-2 ring-[#d0a17b]",
    plate: "from-[#e0b48d] to-[#a97b52]",
    text: "text-[#d0a17b]",
    glow: "shadow-[0_0_34px_rgba(208,161,123,0.16)]",
    avatar: "w-[74px] h-[74px] text-3xl",
  },
};

const PodiumSpot = ({ p, delay }) => {
  const st = PODIUM[p.rank];
  const isFirst = p.rank === 1;
  return (
    <div
      className="flex flex-col items-center justify-end podium-in"
      style={{ animationDelay: `${delay}s` }}
      data-testid={`podium-rank-${p.rank}`}
    >
      {isFirst && (
        <Crown size={30} className="text-[#ffd76b] mb-2 animate-bounce-soft" fill="#ffd76b" />
      )}
      {/* avatar */}
      <div className="relative">
        <div
          className={`rounded-full flex items-center justify-center font-display font-extrabold text-white ${st.avatar} ${st.ring} ${st.glow} ${isFirst ? "champion-pulse" : ""}`}
          style={{ background: avatarBg(p.user) }}
        >
          {initial(p.user)}
        </div>
      </div>
      <p className="mt-3 font-bold text-white text-sm sm:text-base">{showUser(p.user)}</p>
      <p className="text-[11px] text-[#7e879c] font-semibold">{show(p.wagered)} wagered</p>
      <div className="mt-2 mb-3 px-4 py-1.5 rounded-lg bg-[#3ddc84]/10 border border-[#3ddc84]/25">
        <p className="font-display font-extrabold italic text-[#3ddc84] text-base sm:text-lg leading-none">
          {show(p.prize)}
        </p>
      </div>
      {/* podium step */}
      <div
        className={`w-full ${st.height} rounded-t-xl bg-gradient-to-b from-[#262d3f] to-[#1c2231] border border-b-0 border-[#323a4f] relative overflow-hidden`}
      >
        <div className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${st.plate}`} />
        <p
          className={`absolute inset-0 flex items-center justify-center font-display font-extrabold italic ${st.text} ${isFirst ? "text-5xl" : "text-4xl"} opacity-90`}
        >
          {p.rank}
        </p>
      </div>
    </div>
  );
};

/* ---------- Rows 4+ ---------- */
const Row = ({ p, delay }) => (
  <div
    className="row-in group grid grid-cols-[44px_1fr_auto_auto] sm:grid-cols-[54px_1.4fr_1fr_auto] items-center gap-3 sm:gap-5 px-4 sm:px-6 py-4 rounded-xl bg-[#1d2231] border border-[#2c3448] hover:border-[#3ddc84]/40 hover:translate-x-1 transition-all duration-300"
    style={{ animationDelay: `${delay}s` }}
    data-testid={`leaderboard-row-${p.rank}`}
  >
    <span className="inline-flex w-9 h-9 rounded-lg bg-[#232a3d] items-center justify-center text-[13px] font-extrabold text-[#97a1b8] group-hover:text-[#3ddc84] transition-colors duration-200">
      {p.rank}
    </span>
    <div className="flex items-center gap-3 min-w-0">
      <span
        className="shrink-0 w-9 h-9 rounded-full flex items-center justify-center text-[13px] font-extrabold text-white"
        style={{ background: avatarBg(p.user) }}
      >
        {initial(p.user)}
      </span>
      <span className="font-semibold text-white text-sm truncate">{showUser(p.user)}</span>
    </div>
    <div className="hidden sm:flex items-center justify-end gap-2 text-[12px]">
      <span className="uppercase tracking-widest font-bold text-[#5d6579]">Wagered</span>
      <span className="font-semibold text-[#aeb7cc]">{show(p.wagered)}</span>
    </div>
    <span className="justify-self-end px-3.5 py-1.5 rounded-lg bg-[#3ddc84]/10 border border-[#3ddc84]/20 text-sm font-bold text-[#3ddc84]">
      {show(p.prize)}
    </span>
  </div>
);

const LeaderboardPage = () => {
  const { data, loading } = useLeaderboard();
  const { d, h, m, s } = useCountdown(data?.periodEnd);
  const players =
    data?.players ||
    Array.from({ length: 10 }, (_, i) => ({
      rank: i + 1,
      user: null,
      wagered: null,
      prize: null,
    }));

  return (
    <div className="pt-[68px] bg-[#171b26] min-h-screen">
      {/* header */}
      <section className="relative overflow-hidden hero-bg">
        <div className="absolute top-[-40%] left-1/2 -translate-x-1/2 w-[800px] h-[500px] rounded-full opacity-[0.13] blur-[110px] bg-[#3ddc84] pointer-events-none" />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 pt-14 pb-24 text-center">
          <img
            src="/duelbits-logo.png"
            alt="Duelbits"
            className="h-10 sm:h-12 w-auto object-contain mx-auto mb-6"
            data-testid="leaderboard-duelbits-logo"
          />
          <h1
            className="font-display font-extrabold italic text-5xl sm:text-7xl uppercase tracking-tight text-white"
            data-testid="leaderboard-title"
          >
            Leader<span className="text-[#3ddc84]">board</span>
          </h1>
          <p className="mt-4 text-[#97a1b8] max-w-lg mx-auto text-sm sm:text-base">
            Wager on Duelbits under code{" "}
            <span className="text-[#3ddc84] font-bold">“{CODE}”</span> and climb the ranks.
          </p>

          {/* prize pool — big glowing text, no box */}
          <div className="mt-10" data-testid="leaderboard-prize-pool">
            <p className="uppercase text-[11px] font-bold tracking-[0.3em] text-[#7e879c]">
              Monthly prize pool
            </p>
            <p
              className={`glow-text font-display font-extrabold italic text-6xl sm:text-8xl uppercase leading-tight mt-1 ${loading ? "animate-pulse" : ""}`}
            >
              {loading ? "…" : show(data?.prizePool)}
            </p>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0">
          <CutEdge fill="#171b26" className="h-[70px] sm:h-[90px]" />
        </div>
      </section>

      {/* countdown box directly above top 3 */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 pt-10">
        <div className="flex justify-center" data-testid="leaderboard-countdown">
          <div className="inline-flex items-center gap-4 sm:gap-6 px-6 sm:px-8 py-4 rounded-2xl bg-[#1d2231] border border-[#2c3448] shadow-[0_14px_36px_rgba(0,0,0,0.35)]">
            <div className="flex items-center gap-3 sm:gap-5">
              {[
                [d, "Days"],
                [h, "Hrs"],
                [m, "Min"],
                [s, "Sec"],
              ].map(([v, l], i) => (
                <React.Fragment key={l}>
                  {i > 0 && (
                    <span className="font-display font-extrabold text-[#3a4258] text-xl">:</span>
                  )}
                  <div className="text-center min-w-[44px]">
                    <p className="font-display font-extrabold italic text-2xl text-white leading-none">
                      {pad(v)}
                    </p>
                    <p className="mt-1 text-[9px] uppercase tracking-widest font-bold text-[#7e879c]">
                      {l}
                    </p>
                  </div>
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>

        {/* podium — tight below the timer */}
        <div className="grid grid-cols-3 gap-3 sm:gap-6 items-end mt-8">
          <PodiumSpot p={players[1]} delay={0.15} />
          <PodiumSpot p={players[0]} delay={0} />
          <PodiumSpot p={players[2]} delay={0.3} />
        </div>
        <div className="h-1 rounded-full bg-gradient-to-r from-transparent via-[#3ddc84]/50 to-transparent" />
      </section>

      {/* rows 4+ */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 py-12 flex flex-col gap-3">
        {players.slice(3).map((p, i) => (
          <Row key={p.rank} p={p} delay={0.08 * i} />
        ))}
        <p className="mt-6 text-center text-[12px] text-[#5d6579]">
          Rankings update every 5 minutes. Prizes paid out within 24h after the monthly reset.
        </p>
      </section>
    </div>
  );
};

export default LeaderboardPage;
